import React, { useState, useEffect, useCallback, useRef } from 'react';
import { backtestService, backtestJobService } from '../../config/api';
import { formatBacktestDate } from '../../utils/backtestDataTransformer';
import { fetchBacktestJobs, cancelBacktestJob, JOB_STATUS } from '../../services/backtestService';

// SVG Icons
const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const VisibilityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const SortIcon = ({ active, direction }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ml-1 ${active ? 'opacity-100' : 'opacity-30'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    {direction === 'asc' ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    )}
  </svg>
);

/**
 * BacktestHistoryList Component
 * 
 * Displays a list of past backtests with options to view details or delete.
 * 
 * @param {Object} props
 * @param {string} props.strategyId - Optional filter by strategy ID
 * @param {Function} props.onSelect - Callback when a backtest is selected for viewing
 * @param {Function} props.onDelete - Callback after a backtest is deleted
 */
const BacktestHistoryList = ({ strategyId, onSelect, onDelete }) => {
  const [backtests, setBacktests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  // Active jobs state
  const [activeJobs, setActiveJobs] = useState([]);
  const [cancellingJobId, setCancellingJobId] = useState(null);

  // Search & Filter State
  const [nameInput, setNameInput] = useState('');
  const [searchName, setSearchName] = useState('');
  const [jobIdInput, setJobIdInput] = useState('');
  const [searchJobId, setSearchJobId] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = All

  // Server-side Pagination State
  const [currentPage, setCurrentPage] = useState(0); // zero-indexed for API
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Debounce timer ref
  const debounceRef = useRef(null);

  const handleNameInput = (value) => {
    setNameInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchName(value);
      setCurrentPage(0);
    }, 400);
  };

  const handleJobIdInput = (value) => {
    setJobIdInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchJobId(value);
      setCurrentPage(0);
    }, 400);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(0);
  };

  // Fetch backtest jobs via search API
  const fetchBacktests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await backtestJobService.searchJobs({
        jobId: searchJobId || undefined,
        strategyName: searchName || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        size: pageSize,
      });

      setBacktests(response.data || []);
      const meta = response.meta || {};
      setTotalPages(meta.totalPages || 0);
      setTotalItems(meta.totalItems || 0);

      // Also fetch active jobs for the running section
      try {
        const jobs = await fetchBacktestJobs();
        const activeJobList = jobs.filter(j =>
          j.status === JOB_STATUS.PENDING || j.status === JOB_STATUS.RUNNING
        );
        setActiveJobs(activeJobList);
      } catch (jobError) {
        console.error('Error fetching active jobs:', jobError);
      }
    } catch (err) {
      console.error('Error fetching backtest history:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load backtest history');
    } finally {
      setLoading(false);
    }
  }, [searchJobId, searchName, statusFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchBacktests();
  }, [fetchBacktests]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Handle sort
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Client-side sorting (within current page from server)
  const sortedBacktests = [...backtests].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    if (orderBy === 'executedAt' || orderBy === 'startDate' || orderBy === 'endDate' || orderBy === 'createdAt') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }

    if (order === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }
    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
  });

  // Handle delete confirmation
  const handleDeleteClick = (backtest) => {
    setSelectedForDelete(backtest);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedForDelete) return;

    setDeleting(true);
    try {
      await backtestService.delete(selectedForDelete.id);
      setBacktests(prev => prev.filter(b => b.id !== selectedForDelete.id));
      onDelete?.(selectedForDelete.id);
    } catch (err) {
      console.error('Error deleting backtest:', err);
      setError('Failed to delete backtest');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedForDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedForDelete(null);
  };

  // Sortable header component
  const SortableHeader = ({ field, children, align = 'left' }) => (
    <th
      className={`cursor-pointer hover:bg-base-200 ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center ${align === 'right' ? 'justify-end' : ''}`}>
        {children}
        <SortIcon active={orderBy === field} direction={orderBy === field ? order : 'asc'} />
      </div>
    </th>
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <span className="loading loading-spinner loading-md"></span>
        <span className="ml-4">Loading backtest history...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
        <button className="btn btn-ghost btn-sm gap-1" onClick={fetchBacktests}>
          <RefreshIcon /> Retry
        </button>
      </div>
    );
  }

  const hasSearchFilters = searchName || searchJobId || statusFilter;

  // Empty state — only show when no filters are active (first-time / truly empty)
  if (backtests.length === 0 && activeJobs.length === 0 && !hasSearchFilters) {
    return (
      <div className="card bg-base-100 shadow p-8 text-center">
        <p className="text-base-content/60">
          No backtest history found. Run a backtest to see results here.
        </p>
      </div>
    );
  }

  // Handle cancel job
  const handleCancelJob = async (jobId) => {
    setCancellingJobId(jobId);
    try {
      await cancelBacktestJob(jobId);
      setActiveJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (err) {
      console.error('Error cancelling job:', err);
    } finally {
      setCancellingJobId(null);
    }
  };

  // Get job status badge
  const getJobStatusBadge = (status) => {
    const badges = {
      [JOB_STATUS.PENDING]: 'badge-warning',
      [JOB_STATUS.RUNNING]: 'badge-info',
      [JOB_STATUS.COMPLETED]: 'badge-success',
      [JOB_STATUS.FAILED]: 'badge-error',
      [JOB_STATUS.CANCELLED]: 'badge-ghost',
    };
    return badges[status] || 'badge-ghost';
  };

  return (
    <div>
      {/* Header with Search */}
      <div className="mb-4 space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">Backtest History</h2>
          <div className="flex items-center gap-2">
            <span className="badge badge-ghost">Total: {totalItems}</span>
            <button className="btn btn-sm btn-ghost gap-1" onClick={fetchBacktests} disabled={loading}>
              <RefreshIcon /> Refresh
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <input
              type="text"
              placeholder="Search by strategy name..."
              className="input input-bordered input-sm w-full pl-9"
              value={nameInput}
              onChange={(e) => handleNameInput(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Job ID..."
            className="input input-bordered input-sm w-56"
            value={jobIdInput}
            onChange={(e) => handleJobIdInput(e.target.value)}
          />
          <select
            className="select select-bordered select-sm w-40"
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="RUNNING">Running</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Active Jobs Section */}
      {activeJobs.length > 0 && (
        <div className="mb-6 p-4 bg-base-200 rounded-lg">
          <h3 className="font-medium mb-3 flex items-center gap-2">
            <span className="loading loading-spinner loading-sm"></span>
            Running Backtests ({activeJobs.length})
          </h3>
          <div className="space-y-2">
            {activeJobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between bg-base-100 p-3 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{job.strategyName}</span>
                    <span className={`badge badge-sm ${getJobStatusBadge(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-base-content/60">
                    <span>{job.tickerName}</span>
                    <span>{job.interval}</span>
                  </div>
                  {job.status === JOB_STATUS.RUNNING && (
                    <div className="mt-2">
                      <progress
                        className="progress progress-info w-full h-2"
                        value={job.progress || 0}
                        max="100"
                      />
                      <div className="text-xs text-base-content/60 mt-1">
                        {job.progress || 0}% - Day {job.currentChunk || 0} of {job.totalChunks || '?'}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline btn-error"
                  onClick={() => handleCancelJob(job.id)}
                  disabled={cancellingJobId === job.id}
                >
                  {cancellingJobId === job.id ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    'Cancel'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table table-sm bg-base-100">
          <thead>
            <tr>
              <SortableHeader field="id">Job ID</SortableHeader>
              <SortableHeader field="strategyName">Strategy</SortableHeader>
              <SortableHeader field="tickerName">Symbol</SortableHeader>
              <SortableHeader field="interval">Interval</SortableHeader>
              <SortableHeader field="startDate">Start Date</SortableHeader>
              <SortableHeader field="endDate">End Date</SortableHeader>
              <SortableHeader field="createdAt">Created</SortableHeader>
              <th>Progress</th>
              <th>Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBacktests.map((job) => (
              <tr
                key={job.id}
                className={`hover ${job.backtestResultId ? 'cursor-pointer' : 'cursor-default'}`}
                onClick={() => job.backtestResultId && onSelect?.(job.backtestResultId)}
              >
                <td className="text-xs text-base-content/60 font-mono">{job.id}</td>
                <td className="font-medium">{job.strategyName}</td>
                <td>{job.tickerName}</td>
                <td>{job.interval}</td>
                <td className="text-xs">{formatBacktestDate(job.startDate)}</td>
                <td className="text-xs">{formatBacktestDate(job.endDate)}</td>
                <td className="text-base-content/60 text-xs">{formatBacktestDate(job.createdAt)}</td>
                <td>
                  {(job.status === JOB_STATUS.RUNNING || job.status === JOB_STATUS.PENDING) ? (
                    <div className="flex items-center gap-2">
                      <progress
                        className="progress progress-info w-16 h-2"
                        value={job.progress || 0}
                        max="100"
                      />
                      <span className="text-xs text-base-content/60">{job.progress || 0}%</span>
                    </div>
                  ) : (
                    <span className="text-xs text-base-content/40">—</span>
                  )}
                </td>
                <td>
                  <span className={`badge badge-sm ${getJobStatusBadge(job.status)}`}>
                    {job.status}
                  </span>
                </td>
                <td className="text-center">
                  <div className="flex justify-center gap-1">
                    {job.backtestResultId && (
                      <button
                        className="btn btn-ghost btn-xs btn-circle tooltip"
                        data-tip="View Results"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect?.(job.backtestResultId);
                        }}
                      >
                        <VisibilityIcon />
                      </button>
                    )}
                    <button
                      className="btn btn-ghost btn-xs btn-circle text-error tooltip"
                      data-tip="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(job);
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* No results message when filters are active */}
      {backtests.length === 0 && hasSearchFilters && (
        <div className="p-8 text-center text-base-content/60">
          No backtest jobs match your search criteria.
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 0 && (
        <div className="flex justify-center mt-4 pt-4 border-t border-base-300">
          <div className="join">
            <button
              className="join-item btn btn-sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              ‹
            </button>
            <button className="join-item btn btn-sm no-animation cursor-default bg-base-100">
              Page {currentPage + 1} of {totalPages}
            </button>
            <button
              className="join-item btn btn-sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              ›
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Delete Backtest?</h3>
            <p className="py-4">
              Are you sure you want to delete the backtest for "{selectedForDelete?.strategyName}"?
              This action cannot be undone.
            </p>
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn btn-error gap-2"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting && <span className="loading loading-spinner loading-sm"></span>}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleDeleteCancel}>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
};

export default BacktestHistoryList;
